// Static content for the Wald marketing site.
// New company. Honest positioning. No overclaiming.

export const services = [
  {
    slug: "digital-transformation",
    number: "01",
    title: "Digital Transformation",
    tagline: "Legacy system modernization for agricultural operations.",
    summary:
      "Digital transformation for agriculture, done as legacy system modernization — replacing fragmented spreadsheets and disconnected ERPs with one agricultural ERP spine your team can run.",
    outcomes: [
      "One shared view across grower, packhouse, export and finance",
      "Compliance workflow integrated — not added as an afterthought",
      "Data captured once, used everywhere",
    ],
    approach: [
      "Discovery grounded in your operation, not a boilerplate proposal",
      "Rollouts sequenced around packing windows and audit cycles",
      "Incremental delivery with weekly demos on real data",
    ],
  },
  {
    slug: "ai-automation",
    number: "02",
    title: "AI & Automation",
    tagline: "Agriculture AI solutions and agricultural automation that earn their keep.",
    summary:
      "AI for farming and export operations, applied where it earns its keep — document capture, quality assist, precision agriculture-style forecasting and workflow orchestration.",
    outcomes: [
      "Document capture and workflow automation, scoped to each client's requirements",
      "Predictive quality and volume forecasting per orchard",
      "Human-in-the-loop workflows for reconciliation and exceptions",
    ],
    approach: [
      "Explainable model choices — auditable by design",
      "Cost-per-decision economics before any deployment",
      "Human oversight on every high-stakes outcome",
    ],
  },
  {
    slug: "custom-software",
    number: "03",
    title: "Custom Software",
    tagline: "Custom agricultural software built to your operation.",
    summary:
      "When off-the-shelf breaks against reality, we design and build custom agricultural software your operation needs — packhouse floor tooling, grower portals, farm dashboards, exporter reporting and compliance platforms.",
    outcomes: [
      "Fit-for-purpose systems for packing, grading, dispatch and grower engagement",
      "Owned IP and portable codebases — no per-seat licensing",
      "Documented handovers your team can operate independently",
    ],
    approach: [
      "Design-partner engagement — not vendor transaction",
      "Continuous production feedback from the first sprint",
      "Handover-ready code and infrastructure from day one",
    ],
  },
  {
    slug: "platform-engineering",
    number: "04",
    title: "Platform Engineering",
    tagline: "Agricultural cloud solutions and systems integration your team can operate.",
    summary:
      "Observable, secure, portable agricultural cloud solutions — cloud, on-premise or hybrid — with agricultural systems integration across your existing ERP, IoT and compliance tooling. Delivered with runbooks and internal enablement so your team owns the operation.",
    outcomes: [
      "Infrastructure-as-code, versioned alongside application code",
      "POPIA-aware data residency and access controls",
      "Observability from day one — logs, traces, metrics, alerts",
    ],
    approach: [
      "Portability by default — no cloud vendor lock-in",
      "Runbooks and enablement handed to your operators",
      "Continuous, small, reversible deployments",
    ],
  },
];

export const industries = [
  {
    slug: "agricultural-exporters",
    number: "01",
    title: "Agricultural Exporters",
    summary:
      "Multi-market exporters coordinating protocols, currency exposure and packhouse throughput. As an agricultural technology partner, we build the operational core and agricultural data analytics that account for every carton.",
    focus: [
      "Multi-market protocol management",
      "Container-level traceability",
      "Grower payment and settlement flows",
    ],
  },
  {
    slug: "packhouses",
    number: "02",
    title: "Packhouses",
    summary:
      "High-throughput operations where a lost hour is a lost pallet. We digitise grading, dispatch and QC without disrupting floor rhythm.",
    focus: [
      "Realtime line-level throughput dashboards",
      "Digital grading and QC capture",
      "Dispatch and load coordination",
    ],
  },
  {
    slug: "grower-organisations",
    number: "03",
    title: "Grower Organisations",
    summary:
      "Cooperatives and grower bodies coordinating across dozens of orchards, farms and packhouses. We build the farm management software, farm dashboards and shared systems that keep the collective aligned.",
    focus: [
      "Farm management software with grower portals and settlement transparency",
      "Farm dashboards for aggregated volume and quality analytics",
      "Compliance rollup across member operations",
    ],
  },
  {
    slug: "export-agents",
    number: "04",
    title: "Export Agents",
    summary:
      "Agents managing exporter relationships, market allocations and settlement. We build the CRM-to-logistics backbone that makes the middle layer efficient.",
    focus: [
      "Exporter and buyer relationship systems",
      "Allocation and pricing management",
      "Documentation and settlement automation",
    ],
  },
  {
    slug: "regulated-organisations",
    number: "05",
    title: "Regulated Organisations",
    summary:
      "Statutory bodies, industry associations and regulators. We build audit-ready systems where evidence, chain-of-custody and reporting are first-class.",
    focus: [
      "Audit-grade evidence capture",
      "Reporting for regulators and boards",
      "Access-controlled multi-stakeholder workflows",
    ],
  },
];

// Track Record — Certisfy framed strictly as a capability demonstration.
// No implied ownership, no client-relationship claim.
export const trackRecord = [
  {
    kind: "Compliance & Document Workflow",
    client: "Certisfy",
    period: "Reference build",
    logo: "/brand/certisfy-horizontal.webp",
    description:
      "Certisfy is Wald's compliance and document workflow platform — helping operational teams manage compliance records, document handling and review steps in one place.",
  },
  {
    kind: "Packhouse Operations",
    client: "Capability",
    period: "Reference build",
    description:
      "Realtime packhouse throughput, grading and dispatch systems designed for multi-line operations replacing paper-based reconciliation.",
  },
  {
    kind: "Grower Coordination",
    client: "Capability",
    period: "Reference build",
    description:
      "Grower portals and settlement transparency platforms designed for cooperatives coordinating across dozens of orchards.",
  },
];

export const whyWald = [
  {
    title: "Specialist focus",
    body: "Wald is built specifically for agricultural export and adjacent regulated operations. That focus shapes every engagement.",
  },
  {
    title: "Enterprise discipline",
    body: "Documented systems, observability from day one, and clean handovers — built to be operated by your team.",
  },
  {
    title: "Season-aware delivery",
    body: "Rollouts sequenced around packing windows, audits and cash cycles — not calendar quarters.",
  },
  {
    title: "Client-owned outcomes",
    body: "Clients own the code, the data and the infrastructure. Portable by default. No per-seat licensing.",
  },
];

// Verity is no longer part of the public product lineup. Its signing capability
// now lives inside Certisfy's own certificate-issuance workflow rather than being
// offered as a separate product, so it isn't shown on public product surfaces.
export const productVerity = {
  slug: "verity",
  title: "Verity",
  tagline: "Signed documents, trusted anywhere.",
  intro:
    "Verity is Wald's digital signing service for regulated documents. It signs XML and PDF files to the XAdES and PAdES standards, so certificates, inspection reports and other regulated documents carry a legally defensible, cryptographically verifiable signature — without depending on a single third-party signer.",
  pillars: [
    { title: "Standards-based", body: "XAdES (XML) and PAdES (PDF) signing, aligned to what authorities already require." },
    { title: "Built for regulated documents", body: "Export certificates, inspection reports, compliance records." },
    { title: "Volume-based", body: "Pay for what you sign. No per-seat licensing." },
    { title: "Status: in development", body: "Timeline and availability will be published as milestones are reached." },
  ],
};

export const productAgriBrain360 = {
  slug: "agribrain-360",
  title: "Wald AgriBrain 360",
  tagline: "Field data, weather and quality signals in one operating view.",
  intro:
    "Wald AgriBrain 360 aggregates field, sensor and weather data into a single dashboard for farm and packhouse decision-making — surfacing yield trends, early anomaly signals and the kind of day-to-day visibility spreadsheets can't keep up with.",
  pillars: [
    { title: "Unified field dashboard", body: "Weather, field mapping and sensor feeds in one view, per orchard or block." },
    { title: "Trend and anomaly alerts", body: "Configurable alerts on yield, quality and condition signals as data arrives." },
    { title: "Mobile field app", body: "Capture and check field data from the orchard, not just the office." },
  ],
};

// AgriCore ERP and FarmTrace are not part of the current public product portfolio
// (owner-selected lineup: AgriBrain 360, Certisfy, WaldInsights, WaldFlow). Kept here,
// unexported from any public page, in case the owner confirms they should be reinstated.
export const productAgriCoreErp = {
  slug: "agricore-erp",
  title: "Wald AgriCore ERP",
  tagline: "Farm-to-port operations for export packhouses, in one system.",
  intro:
    "Wald AgriCore ERP replaces the spreadsheets and disconnected systems packhouses rely on with one operating core — intake, grading and pallet-level tracking, grower pool accounting, and export consignment management across markets and currencies.",
  steps: [
    { number: "01", title: "Intake", body: "Receive and log incoming product against grower and lot." },
    { number: "02", title: "Grade & pack", body: "Digital grading capture feeds pallet-level stock records directly." },
    { number: "03", title: "Track", body: "Pallet and lot visibility from cold store through to dispatch." },
    { number: "04", title: "Reconcile", body: "Grower pool accounting ties payouts back to graded volumes." },
    { number: "05", title: "Export", body: "Consignment and documentation management across markets and currencies." },
  ],
  modules: [
    "Intake & Grading",
    "Pallet & Lot Tracking",
    "Grower Pool Accounting",
    "Export Consignment Management",
    "Finance & Supplier Ledger",
    "Operational Reporting",
  ],
};

export const productFarmTrace = {
  slug: "farmtrace",
  title: "Wald FarmTrace",
  tagline: "Traceability from farm to shelf, pallet by pallet.",
  intro:
    "Wald FarmTrace tracks produce through repacking, splitting and distribution so exporters can answer a traceability or recall question in minutes, not days — with QR-coded pallet and batch records that hold up under audit.",
  pillars: [
    { title: "Pallet & batch capture", body: "Trace data recorded at every handoff, from pack to port to retail." },
    { title: "QR-coded records", body: "Scannable pallet and batch labels link back to the full trace history." },
    { title: "Audit & recall support", body: "Pull a full chain-of-custody record for any pallet on demand." },
    { title: "Status: early access", body: "Available for pilot with exporters already running AgriCore ERP." },
  ],
};

// Certisfy is positioned as general-purpose compliance and document workflow software.
// It is deliberately not described around certification, inspection, phytosanitary
// documentation or export certification — see wald-website-remediation-brief-for-claude.pdf.
// Capabilities below are kept to the general category the owner confirmed; specific
// feature, pricing and status claims are omitted pending owner confirmation.
export const productCertisfy = {
  slug: "certisfy",
  title: "Certisfy",
  tagline: "Compliance records and document workflow, in one place.",
  intro:
    "Certisfy is Wald's compliance and document workflow platform, built to help operational teams manage compliance records, document handling and review steps without juggling spreadsheets and email threads.",
  url: "https://certisfy.cloud",
  capabilities: [
    "Compliance record management",
    "Document upload and review",
    "Approval workflow and audit trail",
  ],
};

// WaldInsights and WaldFlow are part of the owner-selected product portfolio, but no
// verified feature, status or availability detail exists yet. Deliberately minimal —
// do not add feature claims here without owner confirmation.
export const productWaldInsights = {
  slug: "waldinsights",
  title: "WaldInsights",
  tagline: "Details to be confirmed.",
  intro:
    "WaldInsights is part of Wald's product portfolio. A full description, feature set and availability status will be published once confirmed with the Wald team.",
};

export const productWaldFlow = {
  slug: "waldflow",
  title: "WaldFlow",
  tagline: "Details to be confirmed.",
  intro:
    "WaldFlow is part of Wald's product portfolio. A full description, feature set and availability status will be published once confirmed with the Wald team.",
};

export const homeFaqs = [
  {
    q: "How does a Wald engagement start?",
    a: "With a scoping conversation. If we look like a fit, we run a discovery phase grounded in your operation before proposing anything.",
  },
  {
    q: "Do you replace our existing ERP?",
    a: "Almost never. We integrate with it, then rebuild the parts where it fails — grading, dispatch, grower payments, compliance evidence.",
  },
  {
    q: "Who owns what we pay you to build?",
    a: "You do. Code, data and infrastructure. Documented, portable handovers are standard.",
  },
  {
    q: "How does Wald handle POPIA?",
    a: "By design. Data residency, access controls and audit trails are baked in from the first sprint — not bolted on for compliance reviews.",
  },
];
